import React, { useState } from 'react'
import {
  Button,
  Card,
  Div,
  Flex,
  Grid,
  GridItem,
  Image,
  Logo,
  QuantumThemeWrapper,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { BydesignProductGrid } from '../bydesignProduct/bydesignProductGrid'
import { GetProps } from 'react-redux'
import { useWindowWidth } from '@react-hook/window-size'
import connect from 'src/redux/connect'
import { CreateSparseUser, InviteFragment } from 'src/graphql'
import { performOperation } from 'src/app/api'
import { openApp } from 'src/helpers/appHelpers'
import { Transition } from 'src/chrome'

const open = (href: string) => () => {
  window.location.href = href
}

const openGiftStore = open('https://app.sendoutcards.com/store')

const maxTitleTextWidth = '1400px'
const maxContentWidth = '1600px'

type SmallFeatureProps = {
  imageUrl: string
  title: string
  body: string
  buttonTitle: string
  buttonOnClick: () => void
}

const SmallFeature = ({
  imageUrl: imageSrc,
  title,
  body,
  buttonTitle,
  buttonOnClick,
}: SmallFeatureProps) => (
  <Flex
    flexDirection="column"
    width="340px"
    height="580px"
    inset="x2"
    alignItems="center"
  >
    <Div width="240px" height="300px">
      <Image
        image={{ url: imageSrc }}
        width="100%"
        height="100%"
        isActive={true}
      />
    </Div>
    <Spacer space="x1" />
    <Text type="subtitle">{title}</Text>
    <Text type="body" alignment="center">
      {body}
    </Text>
    <Div flexGrow={1} />
    <Button title={buttonTitle} onClick={buttonOnClick} outlined={true} />
  </Flex>
)

const ProductImageGrid = ({
  imageUrls,
}: {
  imageUrls: [string, string, string, string]
}) => {
  const width = useWindowWidth()
  const getGridWidth = (width: number) => {
    if (width > 1024) {
      return 448
    } else if (width > 767) {
      return 320
    } else if (width > 474) {
      return 448
    } else {
      return 320
    }
  }
  const gridWidth = getGridWidth(width)
  return (
    <Div minWidth={`320px`}>
      <Grid
        columnSize={gridWidth === 320 ? 104 : 208}
        rowSize={gridWidth === 320 ? 54 : 75}
        gap={16}
        maxWidth={gridWidth}
      >
        {imageUrls.map((url, index) => {
          return (
            <GridItem
              key={index}
              gridColSpan={1}
              gridRowSpan={
                index === 0 ? 3 : index === 1 ? 2 : index === 2 ? 3 : 2 // TODO Will need to write a function to place the portrait images in the correct places :(
              }
            >
              <Image
                image={{ url }}
                width="100%"
                height="100%"
                isActive={true}
              />
            </GridItem>
          )
        })}
      </Grid>
    </Div>
  )
}

const Divider = ({ width }: { width?: GetProps<typeof Div>['width'] }) => (
  <Div
    outset={{ vertical: 'x2' }}
    backgroundColor={{ swatch: 'blueScale', shade: '_600' }}
    height="x1"
    width={width}
  />
)

const LargeFeature = ({
  title,
  body,
  buttonTitle,
  buttonOnClick,
  imageUrls,
  flexDirection = 'row',
}: {
  title: string
  body: string
  buttonTitle: string
  buttonOnClick: () => void
  imageUrls: [string, string, string, string]
  flexDirection?: 'row' | 'row-reverse'
}) => (
  <Flex
    inset="x8"
    flexDirection={flexDirection}
    flexWrap="wrap"
    alignItems="center"
    justifyContent="space-around"
    width="100%"
    maxWidth={maxContentWidth}
  >
    <ProductImageGrid imageUrls={imageUrls} />
    <Flex
      flexDirection="column"
      alignItems="start"
      maxWidth="560px"
      minWidth="300px"
      outset="x8"
    >
      <Text type="title">{title}</Text>
      <Divider width="100px" />
      <Text type="body">{body}</Text>
      <Spacer space="x4" />
      <Button title={buttonTitle} onClick={buttonOnClick} fullWidth={false} />
    </Flex>
  </Flex>
)

const StreamCard = ({
  imageUrl,
  brand,
  description,
  onClick,
}: {
  imageUrl: string
  brand: GetProps<typeof Logo>['brand']
  description: string
  onClick?: () => void
}) => (
  <Card
    backgroundColor={!onClick ? 'foreground' : 'background'}
    boxShadow={!onClick ? 'mediumDark' : 'none'}
    maxWidth="220px"
    display="flex"
    justifyContent="center"
    outset="x2"
    cursor={'pointer'}
    onClick={onClick}
  >
    <Flex
      flexDirection="column"
      alignItems="center"
      backgroundColor={!onClick ? 'foreground' : 'background'}
    >
      <Div>
        <Image
          image={{ url: imageUrl }}
          borderRadius="circle"
          width="145px"
          height="145px"
          isActive={true}
        />
      </Div>
      <Spacer space="x4" />
      <Logo brand={brand} color="black" width="145px" />
      <Spacer space="x4" />
      <Text
        type="footnote"
        alignment="center"
        weight="thin"
        lineHeight={1.6}
        content={description}
      />
    </Flex>
  </Card>
)

type ContentProps = {
  invite?: InviteFragment
}

const Content: React.FC<ContentProps> = props => {
  const { invite } = props
  const [isCreatingAccount, setIsCreatingAccount] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const byDesignSubscriptionIDs = [
    { productId: '14547' },
    { productId: '13706' },
    { productId: '13707' },
    { productId: '14866' },
  ]

  const goToApp = (query: { [key: string]: string } = {}) => {
    setIsRedirecting(true)
    openApp(``, query)
  }

  const handleSendFreeCard = () => {
    if (invite) {
      setIsCreatingAccount(true)
      performOperation(CreateSparseUser({ inviteId: invite.id }), result => {
        setIsCreatingAccount(false)
        if (result.value) {
          goToApp({
            access_token: result.value.createSparseUser.account.token,
            postcardPromo: '',
          })
        } else {
          goToApp({ verify: '', postcardPromo: '' })
        }
      })
    } else {
      goToApp({ verify: '', postcardPromo: '' })
    }
  }

  if (isCreatingAccount || isRedirecting) {
    return (
      <Transition
        message={isRedirecting ? 'Loading...' : 'Creating guest account...'}
        messageStyle={{ color: 'black', fontWeight: 500 }}
      />
    )
  }

  return (
    <>
      {/* Have to use Flex here because HStack is not customizable */}
      <Flex
        backgroundImage="static/soc-website/images/hero.jpg"
        backgroundSize="cover"
        justifyContent="flex-end"
        inset={{ horizontal: 'x4', vertical: 'x8' }}
        width="100%"
      >
        <Card
          zIndex={2}
          backgroundColor="background"
          maxWidth="480px"
          inset="x8"
          outset={{ top: 'x8', bottom: 'x4' }}
        >
          {/* Have to use a Flex here because Card does not respect the alignItems prop */}
          <Flex
            flexDirection="column"
            alignItems="start"
            backgroundColor="background"
          >
            <Text type="title">SendOutCards</Text>
            <Spacer space="x1" />
            <Text type="largeBody">
              Creating moments through a tangible touch
            </Text>
            <Spacer space="x2" />
            <Text type="body">
              The world needs what your heart has to give. Act on your
              promptings and reach out in kindness by sending a heartfelt card
              every day.
            </Text>
            <Spacer space="x4" />
            <Button onClick={handleSendFreeCard}>Send a Card</Button>
          </Flex>
        </Card>
      </Flex>
      <Div maxWidth={maxTitleTextWidth}>
        <Text type="title" outset="x8" alignment="center">
          Your personal assistant to creating human connections. We are here to
          deliver your promptings and help you make the difference only you can
          make.
        </Text>
      </Div>
      <Flex
        justifyContent="space-around"
        flexWrap="wrap"
        inset={{ vertical: 'x8' }}
        width="100%"
        maxWidth={maxContentWidth}
      >
        <SmallFeature
          imageUrl="static/soc-website/images/daughter-and-mother.png"
          title="Prompting Keepsake"
          body="Create an impact by transforming your prompting into a keepsake that will last forever. Make it uniquely yours with a personal handwriting font."
          buttonTitle="Create a Heartfelt Card"
          buttonOnClick={handleSendFreeCard}
        />
        <SmallFeature
          imageUrl="static/soc-website/images/coffee-and-laptop.png"
          title="Relationship Development"
          body="Never miss a moment to celebrate special occasions. Stay on top of mind with your relationships through our state of the art relationship management system."
          buttonTitle="Celebrate a Relationship"
          buttonOnClick={handleSendFreeCard}
        />
        <SmallFeature
          imageUrl="static/soc-website/images/young-woman-using-phone.png"
          title="Tangible Touch"
          body="By uniting technology with your heartfelt message, we make it easy to send a card and gift anywhere, any time."
          buttonTitle="Send a Card and Gift"
          buttonOnClick={handleSendFreeCard}
        />
      </Flex>
      <Div maxWidth={maxTitleTextWidth}>
        <Text type="subtitle" outset="x8" alignment="center" isItalic={true}>
          Life is full of opportunities to celebrate others. Use our unique
          system to keep track of each special occasion so you can send the
          perfect card. Who will you reach out to today?
        </Text>
      </Div>
      <LargeFeature
        title="Create Moments That Last Forever"
        body="Our state of the art card editor allows you to customize every greeting card you send. This easy-to-use system helps you create cards that are meaningful and memorable. By adding photos, heartfelt messages, and customizations you can make every card you send unique and personal for each recipient."
        buttonTitle="Send a Card"
        buttonOnClick={handleSendFreeCard}
        imageUrls={[
          'static/soc-website/images/dad-and-mom-holding-child.png',
          'static/soc-website/images/monitor-displaying-editor.png',
          'static/soc-website/images/holly-jolly-card.png',
          'static/soc-website/images/grateful-card.png',
        ]}
      />
      <LargeFeature
        title="Stay Top of Mind. Always."
        body="Greeting cards are perfect for any occasion, no matter how big or small. Our relationship development system keeps important dates top of mind by sending you helpful reminders. You can add notes to the contact card to document memories or meetings. Schedule multiple card sends for thank you's, birthdays, anniversaries, and the holidays, ahead of time with our campaign feature so you can stay focused on your relationships with others and stay top of mind all year long."
        buttonTitle="Send a Card"
        buttonOnClick={handleSendFreeCard}
        imageUrls={[
          'static/soc-website/images/starting-something-now-card.png',
          'static/soc-website/images/sendoutcards-ios-app.png',
          'static/soc-website/images/here-for-you-card.png',
          'static/soc-website/images/thank-you-card.png',
        ]}
        flexDirection="row-reverse"
      />
      <LargeFeature
        title="Gifts for Any Occasion"
        body="We've got the perfect gift for any and every occasion - gifts that will please the mind, heart, soul, or stomach. Include a gift to add a little boost of kindness and appreciation to your next card send!"
        buttonTitle="Shop Gifts"
        buttonOnClick={openGiftStore}
        imageUrls={[
          'static/soc-website/images/best-mom-ever-locket.png',
          'static/soc-website/images/scrumptious-assortment.png',
          'static/soc-website/images/brave-little-monster.png',
          'static/soc-website/images/caramels.png',
        ]}
      />
      <Flex inset="x4" backgroundColor="foreground" width="100%">
        <BydesignProductGrid
          title="Our Monthly Subscriptions"
          description="If you enjoy sending lots of cards, our monthly subscriptions are your best option! Our best value is the $97 Unlimited subscription. View more and choose the best option for you."
          productItems={byDesignSubscriptionIDs}
        />
      </Flex>
      <Text type="title" inset="x2">
        Love What You See?
      </Text>
      <Spacer space="x1" />
      <Text type="body" alignment="center" inset="x2">
        Find out how you can get product for free by referring your friends to
        SendOutCards.
      </Text>
      <Spacer space="x4" />
      <Flex width="100%" justifyContent="center">
        {[
          'static/soc-website/images/you-and-me-card.png',
          'static/soc-website/images/selfie-queen-card.png',
          'static/soc-website/images/dad-and-mom-holding-child.png',
          'static/soc-website/images/world-needs-you-card.png',
          'static/soc-website/images/best-dad-in-the-world-card.png',
        ].map(url => (
          <Div
            key={url}
            width="250px"
            height="350px"
            minWidth="250px"
            outset="x4"
          >
            <Image
              isActive={true}
              image={{ url }}
              width="100%"
              height="100%"
              borderRadius="none"
            />
          </Div>
        ))}
      </Flex>
      <Spacer space="x4" />
      <Button
        outlined={true}
        title="Earn Free Gifts"
        onClick={open(
          'https://www.sendoutcards.com/resources/customer-referral-program/',
        )}
      />
      <Spacer space="x8" />
      <Text type="title" alignment="center" inset="x2">
        Explore Our Other Brands
      </Text>
      <Spacer space="x1" />
      <Div maxWidth="700px" inset="x2">
        <Text type="body" alignment="center">
          SendOutCards is one of our multiple streams of relationship
          development products and services. See how other streams can deepen
          your relationship with self, others and business.
        </Text>
      </Div>
      <Spacer space="x2" />
      <Divider width="260px" />
      <Spacer space="x8" />
      <Flex width="100%" justifyContent="space-around" flexWrap="wrap">
        <StreamCard
          imageUrl="static/soc-website/images/blue42-image.png"
          brand="blue42"
          description="Develop the relationship with yourself and the life you live with health and wellness products."
          onClick={open('https://www.blue42forlife.com')}
        />
        <StreamCard
          imageUrl="static/soc-website/images/promptingu-image.jpg"
          brand="promptingU"
          description="Invest in yourself, enrich your mind, empower your future by building it now."
          onClick={open('https://www.promptingu.com')}
        />
        <StreamCard
          imageUrl="static/soc-website/images/sendoutcards-image.jpg"
          brand="soc"
          description="Connect with others by acting on your promptings and sending heartfelt messages to the world."
        />
        <StreamCard
          imageUrl="static/soc-website/images/bella-cadeaux-image.png"
          brand="bellaCadeaux"
          description="Strengthen your relationship with others through gifts that speak the language of the heart."
          onClick={open('https://www.bellacadeaux.com')}
        />
        <StreamCard
          imageUrl="static/soc-website/images/stream-marketing-image.png"
          brand="streamMarketing"
          description="Serve the World in the way only you can by bringing your business out to those who need it most."
          onClick={open('https://www.streammarketing.com')}
        />
        <StreamCard
          imageUrl="static/soc-website/images/stream-va-image.png"
          brand="streamVA"
          description="Expand your reach by plugging in to the virtual assistant service made to help you connect with the world."
          onClick={open('https://www.streamvirtualassistant.com')}
        />
      </Flex>
      <Spacer space="x8" />
    </>
  )
}

const ContentWithProps = connect(
  state => ({
    invite: state.invites.invite,
  }),
  {},
)(Content)

const LaunchPage = () => (
  <QuantumThemeWrapper>
    <Flex flexDirection="column" alignItems="center" backgroundColor="#f9fafb">
      <ContentWithProps />
    </Flex>
  </QuantumThemeWrapper>
)

export default LaunchPage
